import React, {useLayoutEffect, useRef, useState} from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import {useIntl} from 'react-intl';
import {Subtab, useBillingBlock} from './provider';
import SettingsTab from './settings-tab';
import BlocksTab from './blocks-tab';
import ExemptionsTab from './exemptions-tab';
import ReportTab from './report-tab';
import styles from './billing-block.module.scss';

interface BillingBlockTabProps {
  readonly: boolean;
}

const subtabs: {value: Subtab; labelId: string}[] = [
  {value: 'settings', labelId: 'billingBlockSubtabSettings'},
  {value: 'blocks', labelId: 'billingBlockSubtabBlocks'},
  {value: 'exemptions', labelId: 'billingBlockSubtabExemptions'},
  {value: 'report', labelId: 'billingBlockSubtabReport'},
];

const BillingBlockTab = ({readonly}: BillingBlockTabProps) => {
  const intl = useIntl();
  const {subtab, setSubtab, confirmDiscard} = useBillingBlock();
  const listRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{x: number; y: number; width: number; height: number} | null>(null);

  // The white pill is one element that follows the active trigger, so changing subtab slides it.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const place = () => {
      const active = list.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
      if (!active) return;
      setPill({x: active.offsetLeft, y: active.offsetTop, width: active.offsetWidth, height: active.offsetHeight});
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(list);
    return () => observer.disconnect();
  }, [subtab]);

  return (
    // Manual activation: with automatic activation the focus that follows a click would ask to discard twice.
    <Tabs.Root
      value={subtab}
      activationMode="manual"
      onValueChange={(value) => {
        confirmDiscard(() => setSubtab(value as Subtab));
      }}
    >
      <Tabs.List ref={listRef} className={styles.tabList} aria-label={intl.formatMessage({id: 'billingBlockTab'})}>
        {pill && (
          <span
            aria-hidden
            className={styles.tabPill}
            style={{width: pill.width, height: pill.height, transform: `translate(${pill.x}px, ${pill.y}px)`}}
          />
        )}
        {subtabs.map(({value, labelId}) => (
          <Tabs.Trigger key={value} value={value} className={styles.tabTrigger}>
            {intl.formatMessage({id: labelId})}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      <Tabs.Content value="settings" className={styles.tabContent}><SettingsTab readonly={readonly} /></Tabs.Content>
      <Tabs.Content value="blocks" className={styles.tabContent}><BlocksTab readonly={readonly} /></Tabs.Content>
      <Tabs.Content value="exemptions" className={styles.tabContent}><ExemptionsTab readonly={readonly} /></Tabs.Content>
      <Tabs.Content value="report" className={styles.tabContent}><ReportTab /></Tabs.Content>
    </Tabs.Root>
  );
};

export default BillingBlockTab;
